import React, { useContext } from "react";
import CardApplicationAPI from "../../api/commercial/card-application";
import {
  ApplicationFlows,
  ApplicationFlowSteps,
} from "../../route/application/authenticated/application-flow/types";
import { ErrorContext } from "../../route/error/provider";
import { CardholderModel } from "./cardholder-form/types";
import CardholderBasicInformation from "./index";
import CardholderBasicMapper from "./mapper";
import { CardholderPageModel } from "./types";
import { LanguageContext } from "../../context/languageContext";
import { LegalForm } from "../home/types";
import {
  BasicAccountApplicationDocument,
  CardApplicationDocument,
} from "../../api/commercial/generated-types";
import { ProductRefType } from "../../context/product-context/types";

export default function CardholderBasicInformationAdapter({
  flowType,
  basicAccountApplication,
  cardApplication,
  triggerReload,
  setStep,
}: {
  flowType: ApplicationFlows;
  basicAccountApplication: BasicAccountApplicationDocument;
  cardApplication: CardApplicationDocument;
  triggerReload: () => void;
  setStep: (step: ApplicationFlowSteps) => void;
}): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const { setError } = useContext(ErrorContext);

  const selectedProduct = (
    basicAccountApplication
      ? basicAccountApplication.productRef
      : cardApplication.productRef
  ) as ProductRefType;

  const basicApplicationRef = basicAccountApplication
    ? basicAccountApplication.applicationRef
    : cardApplication.basicAccountApplicationRef;

  const company = basicAccountApplication
    ? basicAccountApplication.company
    : cardApplication.company;

  const isSoleProprietorship =
    company.legalForm === LegalForm.SOLE_PROPRIETORSHIP;

  const hasDecentralizedInvoicing = basicAccountApplication
    ? basicAccountApplication.hasDecentralizedInvoicing
    : cardApplication.hasDecentralizedInvoicing;

  const limit = basicAccountApplication
    ? basicAccountApplication.limit
    : cardApplication.basicAccountApplicationLimit;

  const { employmentInfo } = cardApplication || {
    employmentInfo: null,
  };

  const adminData: CardholderModel = basicAccountApplication
    ? CardholderBasicMapper.mapNaturalPersonDocumentToCardholderPageModel(
        basicAccountApplication.admin,
        language,
        employmentInfo,
      )
    : CardholderBasicMapper.mapNaturalPersonDocumentToCardholderPageModel(
        cardApplication.admin,
        language,
        employmentInfo,
      );

  const cardholderApplicationRef = cardApplication
    ? cardApplication.applicationRef
    : null;

  const cardholderModel: CardholderPageModel = cardApplication
    ? CardholderBasicMapper.mapDocumentToPageModel(cardApplication)
    : null;

  return (
    <CardholderBasicInformation
      flowType={flowType}
      initial={cardholderModel}
      context={{
        adminData,
        companyName: company.name,
        isSoleProprietorship,
        selectedProduct,
        hasDecentralizedInvoicing,
        limit,
      }}
      previous={() => {
        setStep(ApplicationFlowSteps.COMPANY_ONLINE);
      }}
      next={async (model: CardholderPageModel) => {
        try {
          const cardApplicationRequest =
            CardholderBasicMapper.mapPageModelToRequest(model, selectedProduct);

          if (flowType === ApplicationFlows.ADDITIONAL_CA) {
            await CardApplicationAPI.updateAdditionalCardholder(
              cardApplicationRequest,
              basicApplicationRef,
              cardholderApplicationRef,
            );
          } else {
            const isFirstCardApplication =
              flowType === ApplicationFlows.FIRST_BAA ||
              flowType === ApplicationFlows.ADDITIONAL_BAA;

            if (isFirstCardApplication) {
              if (cardApplication) {
                await CardApplicationAPI.updateFirstCardholder(
                  cardApplicationRequest,
                  basicApplicationRef,
                  cardholderApplicationRef,
                );
              } else {
                await CardApplicationAPI.startFirstCardApplication(
                  cardApplicationRequest,
                  basicApplicationRef,
                );
              }
            }
          }
        } catch (e) {
          setError(e);
        }

        triggerReload();
      }}
    />
  );
}
